import styled from '@emotion/styled';
import useWindowSize from '@hooks/useWindowSize';
import { mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import {
  Dialog,
  DialogHeader,
  DialogContent,
  DialogHeading
} from 'aquilla/core/molecules/Dialog';
import Cross from 'aquilla/icons/misc/Cross';
import { useRef } from 'react';
import AquillaDrawer from 'aquilla/core/organisms/Drawer';
import useCompete from '@stores/compete';
import useIntersection from '@hooks/useIntersection';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import isEmptyObject from '@utils/isEmptyObject';
import { getIsLoggedIn, getMe } from '@api/selectors/auth';
import { getNoRatingState } from '@cont/Compete/utils';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import Leaderboard from './Leaderboard';
import Filters from './Filters';
import LeaderboardItem from './LeaderboardItem';
import useFilter from './useFilter';

const StyledDialog = styled(Dialog)`
  height: 640px;
  position: relative;
`;

const StyledFilters = styled(Filters)`
  margin-top: 0;
  margin-left: ${Spacings.SPACING_6B};
  & > :first-child {
    margin-right: ${Spacings.SPACING_4B};
  }
  ${mediaQuery.uptoMobile} {
    margin: ${Spacings.SPACING_4B};
    margin-top: 0;
  }
`;

const StyAquillaDrawer = styled(AquillaDrawer)`
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-top: 0;
    & > .aquilla-drawer-header {
      padding: ${Spacings.SPACING_4B};
      width: 10px;
    }
    & > .aquilla-drawer-content {
      padding: 0;
    }
    max-height: 100vh;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  padding: 0;
  height: 640px;
`;

const HeaderWrapper = styled.div`
  grid-column: span 24;
  position: sticky;
  z-index: 1;
  display: flex;
  align-items: center;
  height: 56px;
  justify-content: left;
  width: 100%;
  padding: ${Spacings.SPACING_4B};
  top: 0;
  background: var(--color-base-1);
`;

const Heading = styled(Typography)`
  margin-right: ${Spacings.SPACING_2B};
  margin-left: ${Spacings.SPACING_4B};
`;

const MeDataWrapper = styled.div`
  position: absolute;
  width: 100%;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  background: ${({ $isDarkModeOn }) =>
    $isDarkModeOn ? 'var(--color-base-1)' : 'var(--color-i-white)'};
`;
const LeaderboardWrapper = styled.div`
  position: relative;
`;

const MeWrapperDrawer = styled.div`
  position: fixed;
  width: 100%;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  background: ${({ $isDarkModeOn }) =>
    $isDarkModeOn ? 'var(--color-base-1)' : 'var(--color-i-white)'};
`;

const DrawerWrapper = styled.div`
  height: 100vh;
  margin: 0;
`;

const LeaderboardDialog = ({ data, isValidating, size, setSize, myData }) => {
  const dialogRef = useRef(null);
  const { isMobile } = useWindowSize();
  const { intersecting: isMeIntersecting, ref } = useIntersection();

  const {
    isLeaderboardDialogOpen,
    setLeaderboardDialogState,
    examFilter,
    stateFilter
  } = useCompete();
  const meInfo = useMyInfo(getMe);
  const isLoggedIn = useMyInfo(getIsLoggedIn);

  const { data: filterData } = useFilter();
  const { userPreference } = filterData;

  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);

  const closeDialog = () => {
    setLeaderboardDialogState(false);
    document.body.style.overflow = 'unset';
  };

  const isNoRankState = () => {
    return (
      isLoggedIn &&
      (userPreference.stateCode === stateFilter.code ||
        stateFilter.id === -1) &&
      (examFilter.id === -1 ||
        userPreference.examPreferenceIds?.includes(examFilter.id)) &&
      isEmptyObject(myData)
    );
  };

  if (isMobile) {
    return (
      <DrawerWrapper>
        <StyAquillaDrawer
          anchor="right"
          open={isLeaderboardDialogOpen}
          close={closeDialog}
          showCloseIcon={!isMobile}
        >
          <HeaderWrapper>
            <Cross onClick={closeDialog} height={24} width={24} />
            <Heading variant="h5">Leaderboard</Heading>
          </HeaderWrapper>
          <LeaderboardWrapper>
            <StyledFilters />
            <Leaderboard
              source="Modal"
              leaderboard={{ ...data, isFetching: isValidating }}
              size={size}
              setSize={setSize}
              myData={myData}
              ref={ref}
            />
          </LeaderboardWrapper>
        </StyAquillaDrawer>
        {(isNoRankState() || (myData?.rank && !isMeIntersecting)) && (
          <MeWrapperDrawer $isDarkModeOn={isDarkModeOn}>
            <LeaderboardItem
              item={isNoRankState() ? getNoRatingState(meInfo) : myData}
              source="Modal"
            />
          </MeWrapperDrawer>
        )}
      </DrawerWrapper>
    );
  }

  return (
    <>
      <StyledDialog
        size="medium"
        open={isLeaderboardDialogOpen}
        close={closeDialog}
      >
        <DialogHeader>
          <Cross height={24} width={24} onClick={closeDialog} />

          <DialogHeading>Leaderboard</DialogHeading>
        </DialogHeader>
        <StyledDialogContent ref={dialogRef}>
          <StyledFilters />
          <Leaderboard
            source="Modal"
            leaderboard={{ ...data, isFetching: isValidating }}
            dialogRef={dialogRef}
            size={size}
            setSize={setSize}
            myData={myData}
            ref={ref}
          />
          {(isNoRankState() || (myData?.rank && !isMeIntersecting)) && (
            <MeDataWrapper $isDarkModeOn={isDarkModeOn}>
              <LeaderboardItem
                item={isNoRankState() ? getNoRatingState(meInfo) : myData}
                source="Modal"
              />
            </MeDataWrapper>
          )}
        </StyledDialogContent>
      </StyledDialog>
    </>
  );
};

export default LeaderboardDialog;
